import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router";

import { ApiError } from "@/api/client";
import type { Company, Contact } from "@/api/queries";
import { useBranches, useClaimCompany, useCompany } from "@/api/queries";
import { useMe } from "@/auth/useMe";
import { NuevoSeguimiento } from "@/actividades/NuevoSeguimiento";
import { CompanyTags } from "@/components/CompanyTags";
import { Timeline } from "@/components/Timeline";
import {
  Button,
  CreatedBy,
  EmptyState,
  ErrorState,
  Page,
  Reserved,
  Spinner,
  StatusBadge,
  Table,
  Tag,
} from "@/components/ui";
import { AgregarCanales } from "@/empresas/AgregarCanales";
import { CompanyContacts } from "@/empresas/ContactCard";
import { NuevaEmpresa } from "@/empresas/NuevaEmpresa";
import { NuevaSucursal } from "@/empresas/NuevaSucursal";
import { NuevoContacto } from "@/empresas/NuevoContacto";
import { formatDate } from "@/lib/format";
import { NotFound } from "./NotFound";

/**
 * Screen 04 of the Modernist design — the company file (.docs/08 §7 F3).
 *
 * The densest screen in the design: a KPI band, the follow-up timeline, five
 * collapsible sections — the contact cards first among them — and three
 * modals. Two things shape it more than the layout does.
 *
 * **It does not open for a colleague's company.** A seller reaches their own
 * accounts and the unclaimed ones; management reaches all of them. Anyone else
 * arriving at a colleague's id — a pasted link, an old bookmark — gets the
 * server's own sentence and the way back, not an empty file (D25). So there is
 * no read-only mode here: whoever sees the screen may work on the account.
 *
 * **The collapsibles are closed by design, and that is what makes the screen
 * work on a phone** (.docs/08 §6). They are not lazy-loaded: the branches are
 * one small request the KPI band has already made the seller wait for, the
 * giros arrive named on the company itself, and deferring either would trade a
 * spinner for a second spinner. The contacts are the exception on both counts:
 * the design drew them open, and they were closed because an account with
 * forty people buried everything under it; and they are asked for only when
 * opened, because that list is the one request here that grows with the
 * account.
 */

export function DetalleEmpresa() {
  const { id } = useParams();
  const companyId = Number(id);
  // Validated before any hook runs: hooks cannot sit behind a conditional
  // return, so checking inside the view would mean `useCompany(NaN)` had
  // already fired a request for `/api/companies/NaN/`.
  if (!Number.isInteger(companyId) || companyId <= 0) return <NotFound />;
  return <DetalleEmpresaView companyId={companyId} />;
}

type Modal = "none" | "contact" | "channels" | "branch" | "general" | "followUp";

function DetalleEmpresaView({ companyId }: { companyId: number }) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const [params, setParams] = useSearchParams();
  const company = useCompany(companyId);
  const [modal, setModal] = useState<Modal>(
    // `?sucursal=1` — where "Guardar y agregar sucursal" on screen 08 lands.
    params.get("sucursal") ? "branch" : "none",
  );
  const [editing, setEditing] = useState<Contact | undefined>(undefined);

  const data = company.data;
  const unassigned = data?.assignment_rank === 3;

  const branches = useBranches(companyId);
  const claim = useClaimCompany();

  function close() {
    setModal("none");
    setEditing(undefined);
    if (params.has("sucursal")) {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.delete("sucursal");
          return next;
        },
        { replace: true },
      );
    }
  }

  if (company.isPending) return <Spinner label={t("companies.loadingOne")} />;
  // Somebody else's account. Said as what it is, with the way back and no
  // retry: asking again gets the same answer.
  if (company.error instanceof ApiError && company.error.status === 403) {
    return (
      <div className="mx-auto max-w-[1440px] p-6">
        <EmptyState>
          <span role="alert">{company.error.message}</span>{" "}
          <Link className="underline" to="/prospectos">
            {t("companyDetail.backToProspects")}
          </Link>
        </EmptyState>
      </div>
    );
  }
  if (company.isError || data === undefined) {
    return (
      <div className="mx-auto max-w-[1440px] p-6">
        <ErrorState
          message={t("companies.loadOneFailed")}
          onRetry={() => void company.refetch()}
        />
      </div>
    );
  }

  const branchRows = branches.data?.results ?? [];
  const headquarters = branchRows[0];

  return (
    <Page
      title={
        <span className="flex flex-wrap items-center gap-3">
          {data.legal_name}
          <StatusBadge status={data.status} label={data.status_display} />
          <CompanyTags company={data} />
        </span>
      }
      subtitle={
        <Link to="/prospectos" className="no-underline hover:underline">
          {t("companyDetail.breadcrumb")}
        </Link>
      }
      actions={
        <>
          {/* Group 3 of screen 02, one click from the file itself: a prospect
                nobody owns is usually claimed after it has been opened. */}
          {unassigned ? (
            <Button
              variant="primary"
              disabled={claim.isPending}
              onClick={() => claim.mutate(companyId)}
            >
              {claim.isPending ? t("prospects.claiming") : t("prospects.claim")}
            </Button>
          ) : null}
          <Button onClick={() => setModal("followUp")}>
            {t("companyDetail.addFollowUp")}
          </Button>
          {/* Reserved rather than omitted: the design's primary action on
                this screen is a quote, and there is no quote model (D22). */}
          <Button variant="primary" disabled title={t("reserved.title")}>
            {t("companyDetail.addQuote")}
          </Button>
        </>
      }
    >
      <div className="mb-2 flex flex-wrap gap-2">
        <Tag tone="outline">
          {t("companyDetail.clientSince", {
            year: new Date(data.created).getFullYear(),
          })}
        </Tag>
        <Tag>
          {t("companyDetail.summary", {
            branches: data.branch_count,
            contacts: data.contact_count,
          })}
        </Tag>
        {unassigned ? <Tag>{t("companyDetail.unassignedHint")}</Tag> : null}
        {/* A colleague clicked first. The file re-reads as theirs either way;
            this says why the buttons just went away. */}
        {claim.data?.lost ? (
          <span role="alert" className="text-sm text-brand-700">
            {t("companyDetail.claimLost", {
              agent: claim.data.company.current_agent_name ?? "—",
            })}
          </span>
        ) : null}
        {claim.isError ? (
          <span role="alert" className="text-sm text-brand-700">
            {claim.error.message}
          </span>
        ) : null}
      </div>

      <CreatedBy
        created={data.created}
        by={data.created_by_agent_name}
        className="mb-6"
      />

      {/* KPI band. Four figures from one request — the counts are annotated on
          the company itself, not fetched as three more lists (.docs/08 §7 F3). */}
      <div className="mb-8 grid grid-cols-2 border-y-2 border-rule lg:grid-cols-4">
        <Kpi label={t("companyDetail.kpiBranches")}>{data.branch_count}</Kpi>
        <Kpi label={t("companyDetail.kpiContacts")}>{data.contact_count}</Kpi>
        <Kpi label={t("companyDetail.kpiActivities")}>{data.activity_count}</Kpi>
        <Kpi label={t("companyDetail.kpiLastContact")} accent>
          {data.last_activity_at
            ? formatDate(data.last_activity_at)
            : t("companyDetail.never")}
        </Kpi>
      </div>

      <Collapsible
        title={t("companyDetail.contacts")}
        count={t("companyDetail.people", { count: data.contact_count })}
        summary={t("companyDetail.contactsSummary")}
        showLabel={t("companyDetail.contactsShow")}
        hideLabel={t("companyDetail.contactsHide")}
        action={
          <Button variant="ghost" onClick={() => setModal("contact")}>
            {t("companyDetail.addContact")}
          </Button>
        }
      >
        <CompanyContacts
          companyId={companyId}
          branches={branchRows}
          canManage={me?.is_manager ?? false}
          onEdit={(contact) => {
            setEditing(contact);
            setModal("contact");
          }}
          onAddChannels={(contact) => {
            setEditing(contact);
            setModal("channels");
          }}
        />
      </Collapsible>

      <Section
        title={t("companyDetail.timeline")}
        count={t("companyDetail.records", { count: data.activity_count })}
      >
        <Timeline scope={{ on: "company", id: companyId }} />
      </Section>

      <Collapsible
        title={t("companyDetail.generalTitle")}
        summary={t("companyDetail.generalSummary")}
        showLabel={t("companyDetail.generalShow")}
        hideLabel={t("companyDetail.generalHide")}
        action={
          <Button variant="ghost" onClick={() => setModal("general")}>
            {t("companyDetail.editGeneral")}
          </Button>
        }
      >
        <dl className="grid gap-x-7 gap-y-4 sm:grid-cols-2">
          <Datum label={t("companyDetail.legalName")}>{data.legal_name}</Datum>
          <Datum label={t("companyDetail.rfc")}>{data.rfc || t("common.none")}</Datum>
          <Datum label={t("companyDetail.headquarters")}>
            {headquarters
              ? [headquarters.address, headquarters.neighborhood]
                  .filter(Boolean)
                  .join(", ") || headquarters.name
              : t("common.none")}
          </Datum>
          <Datum label={t("companyDetail.switchboard")}>
            {headquarters?.sales_phone || t("common.none")}
          </Datum>
          <Datum label={t("companyDetail.website")}>
            {data.website ? (
              // `noreferrer` as well as `noopener`: the target learns the CRM's
              // URL otherwise, and these are client sites.
              <a
                className="underline"
                href={data.website}
                target="_blank"
                rel="noopener noreferrer"
              >
                {data.website}
              </a>
            ) : (
              t("common.none")
            )}
          </Datum>
          <Datum label={t("companyDetail.employees")}>
            {data.employees || t("common.none")}
          </Datum>
          <Datum label={t("companyDetail.agent")}>
            {data.current_agent_name ?? t("common.none")}
          </Datum>
          <Datum label={t("companyDetail.origin")}>
            {data.trade_show ? String(data.trade_show) : t("common.none")}
          </Datum>
          <div className="sm:col-span-2">
            <Datum label={t("companyDetail.notes")}>
              {data.notes || t("common.none")}
            </Datum>
          </div>
        </dl>
      </Collapsible>

      <Collapsible
        title={t("companyDetail.branchesTitle")}
        count={t("companyDetail.registered", { count: data.branch_count })}
        summary={t("companyDetail.branchesSummary")}
        showLabel={t("companyDetail.branchesShow")}
        hideLabel={t("companyDetail.branchesHide")}
        action={
          <Button variant="ghost" onClick={() => setModal("branch")}>
            {t("companyDetail.addBranch")}
          </Button>
        }
      >
        {branchRows.length === 0 ? (
          <EmptyState>{t("companyDetail.noBranches")}</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <th className="w-1/2">{t("companyDetail.branchName")}</th>
                <th className="w-[26%]">{t("companyDetail.branchPhone")}</th>
                <th className="w-[22%]">{t("companyDetail.branchKind")}</th>
              </tr>
            </thead>
            <tbody>
              {branchRows.map((branch) => (
                <tr key={branch.id}>
                  <td className="font-semibold">
                    {branch.name}
                    {branch.location_detail ? (
                      <div className="text-[11px] font-normal text-mute-600">
                        {branch.location_detail.short_city}
                      </div>
                    ) : null}
                  </td>
                  <td className="tabular-nums">
                    {branch.sales_phone || t("common.none")}
                  </td>
                  <td>
                    <Tag tone={branch.kind === 1 ? "accent" : "neutral"}>
                      {branch.kind_display}
                    </Tag>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Collapsible>

      <Collapsible
        title={t("companyDetail.girosTitle")}
        count={t("companyDetail.giros", { count: data.business_lines_detail.length })}
        summary={t("companyDetail.girosSummary")}
        showLabel={t("companyDetail.girosShow")}
        hideLabel={t("companyDetail.girosHide")}
      >
        {data.business_lines_detail.length === 0 ? (
          <EmptyState>{t("companyDetail.noGiros")}</EmptyState>
        ) : (
          <div className="flex flex-wrap gap-2.5">
            {/* Named by the server. They used to be looked up in the giro
                catalogue, whose first page holds 50 of ~9,900 — so almost every
                company printed an id where its giro should be. */}
            {data.business_lines_detail.map((giro) => (
              <span
                key={giro.id}
                className="border-2 border-rule bg-panel px-3.5 py-1.5 text-[13px]"
              >
                {giro.name}
              </span>
            ))}
          </div>
        )}
      </Collapsible>

      <section className="border-t-2 border-rule py-6">
        <Reserved label={t("companyDetail.spacesTitle")}>
          {t("companyDetail.spacesReserved")}
        </Reserved>
      </section>

      {/* One at a time, and only the open one. Four dialogs mounted together
          each run their own pickers — locations, giros, industrias, expos —
          on a screen where none of them is on show, and their fields collide
          in the accessibility tree while they are at it. A closed `<dialog>`
          is `display:none`, so nothing is lost by not rendering it. */}
      {modal === "contact" ? (
        <NuevoContacto
          open
          onClose={close}
          companyId={companyId}
          companyName={data.legal_name}
          branches={branchRows}
          {...(editing ? { contact: editing } : {})}
        />
      ) : null}
      {modal === "channels" && editing ? (
        <AgregarCanales onClose={close} companyId={companyId} contact={editing} />
      ) : null}
      {modal === "branch" ? (
        <NuevaSucursal
          open
          onClose={close}
          companyId={companyId}
          companyName={data.legal_name}
        />
      ) : null}
      {modal === "general" ? (
        <NuevaEmpresa open onClose={close} company={data} />
      ) : null}
      {modal === "followUp" ? (
        <NuevoSeguimiento
          open
          onClose={close}
          company={companyId}
          companyName={data.name || data.legal_name}
        />
      ) : null}
    </Page>
  );
}

function Kpi({
  label,
  accent = false,
  children,
}: {
  label: string;
  accent?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="border-r border-b border-rule px-5 py-4 last:border-r-0 lg:border-b-0">
      {/* `-700`, not the accent itself: the system's readme puts the
          accent-to-ground pair at 3:1, which carries icons and large type and
          not a thirteen-pixel label (.docs/08 §7 F6). */}
      <p className={`kicker mb-2 ${accent ? "text-brand-700" : "text-mute-600"}`}>
        {label}
      </p>
      <p
        className={`font-display text-[26px] leading-none font-extrabold sm:text-[30px] ${
          accent ? "text-brand-700" : ""
        }`}
      >
        {children}
      </p>
    </div>
  );
}

function Section({
  title,
  count,
  action,
  children,
}: {
  title: string;
  count?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="border-t-2 border-rule py-6">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-baseline gap-3">
          <h2 className="text-[19px]">{title}</h2>
          {count ? <span className="text-xs text-mute-600">{count}</span> : null}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

/**
 * Closed by default, and that is the design's decision rather than ours:
 * sections a seller opens when they need them is what keeps this screen usable
 * at 390px (.docs/08 §6).
 */
function Collapsible({
  title,
  count,
  summary,
  showLabel,
  hideLabel,
  action,
  children,
}: {
  title: string;
  count?: string;
  summary: string;
  showLabel: string;
  hideLabel: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <section className="border-t-2 border-rule py-6">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-baseline gap-3">
          <h2 className="text-[19px]">{title}</h2>
          {count ? <span className="text-xs text-mute-600">{count}</span> : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button aria-expanded={open} onClick={() => setOpen((was) => !was)}>
            {open ? hideLabel : showLabel}
          </Button>
          {action}
        </div>
      </header>
      {open ? children : <p className="text-[13px] text-mute-600">{summary}</p>}
    </section>
  );
}

function Datum({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="kicker mb-1 text-mute-600">{label}</dt>
      <dd className="text-sm break-words">{children}</dd>
    </div>
  );
}
